// Localização: frontend/src/components/Prontuario/PacienteAgendamentosTab.jsx (Versão 1.0.0)

import React, { useState, useEffect, useCallback } from 'react';
import { getAgendamentos } from '../../api/api';
import LoadingIndicator from '../Common/LoadingIndicator';
import Alert from '../UI/Alert';
import Title from '../Layout/Title';
import Card from '../UI/Card';
import PeriodoFilterButton from './PeriodoFilterButton';
import { Calendar, Clock, User } from 'lucide-react';
import { parseLocalDateTime } from '../../utils/formatters';

const STATUS_COLORS = {
    'Pendente': 'bg-yellow-100 text-yellow-800 border-yellow-300',
    'Em Andamento': 'bg-emerald-100 text-emerald-800 border-emerald-300',
    'Concluído': 'bg-green-100 text-green-800 border-green-300',
    'Cancelado': 'bg-red-100 text-red-800 border-red-300'
};

const PacienteAgendamentosTab = ({ pacienteId }) => {
    const [agendamentos, setAgendamentos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [periodo, setPeriodo] = useState('');
    const [periodoStartDate, setPeriodoStartDate] = useState('');
    const [periodoEndDate, setPeriodoEndDate] = useState('');

    const fetchAgendamentos = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                paciente_id: pacienteId // Filtrar por paciente no backend
            };
            
            // Adicionar filtros de data se fornecidos
            if (periodoStartDate) {
                params.start = periodoStartDate;
            }
            if (periodoEndDate) {
                params.end = periodoEndDate;
            }

            const response = await getAgendamentos(params);
            let agendamentosData = response.data || [];

            // Ordenar por data (mais recentes primeiro) - backend já ordena, mas garantindo aqui também
            // CORREÇÃO: Usar parseLocalDateTime para garantir que a hora seja interpretada corretamente
            agendamentosData.sort((a, b) => {
                const dateA = parseLocalDateTime(a.data_hora_inicio);
                const dateB = parseLocalDateTime(b.data_hora_inicio);
                if (!dateA || !dateB) return 0;
                return dateB.getTime() - dateA.getTime();
            });

            setAgendamentos(agendamentosData);
        } catch (err) {
            console.error('Erro ao buscar agendamentos:', err);
            setError(err.response?.data?.message || 'Erro ao carregar agendamentos.');
        } finally {
            setLoading(false);
        }
    }, [pacienteId, periodoStartDate, periodoEndDate]);

    useEffect(() => {
        fetchAgendamentos();
    }, [fetchAgendamentos]);

    // Handler para mudanças no período
    const handlePeriodoChange = useCallback((newPeriodo) => {
        setPeriodo(newPeriodo || '');
    }, []);

    const handleDateRangeChange = useCallback((newStartDate, newEndDate) => {
        setPeriodoStartDate(newStartDate || '');
        setPeriodoEndDate(newEndDate || '');
    }, []);


    const formatarDataHora = (dataString) => {
        if (!dataString) return 'N/A';
        try {
            // CORREÇÃO: Usar parseLocalDateTime para garantir que a hora seja interpretada corretamente
            const data = parseLocalDateTime(dataString);
            if (!data || isNaN(data.getTime())) return 'N/A';
            return data.toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return 'N/A';
        }
    };

    const formatarPeriodo = (inicio, fim) => {
        if (!inicio) return 'N/A';
        const inicioStr = formatarDataHora(inicio);
        if (!fim) return inicioStr;
        // CORREÇÃO: Usar parseLocalDateTime para garantir que a hora seja interpretada corretamente
        const fimDate = parseLocalDateTime(fim);
        if (!fimDate || isNaN(fimDate.getTime())) return inicioStr;
        const fimStr = fimDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
        return `${inicioStr} - ${fimStr}`;
    };

    if (loading) {
        return <LoadingIndicator message="Carregando agendamentos..." />;
    }

    if (error) {
        return <Alert type="error" message={error} />;
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <Title text="Histórico de Agendamentos" size="small" />
                <PeriodoFilterButton
                    periodo={periodo}
                    startDate={periodoStartDate}
                    endDate={periodoEndDate}
                    onPeriodoChange={handlePeriodoChange}
                    onDateRangeChange={handleDateRangeChange}
                />
            </div>

            {/* Lista de Agendamentos */}
            {agendamentos.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                    <Calendar className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-600 text-lg font-medium">
                        Nenhum agendamento encontrado.
                    </p>
                    <p className="text-gray-500 text-sm mt-2">
                        Os agendamentos deste paciente aparecerão aqui.
                    </p>
                </div>
            ) : (
                <Card title={`Agendamentos (${agendamentos.length} ${agendamentos.length === 1 ? 'agendamento' : 'agendamentos'})`}>
                    {/* Desktop - Tabela */}
                    <div className="hidden md:block overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-emerald-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-emerald-900 uppercase tracking-wider">
                                        Data/Hora
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-emerald-900 uppercase tracking-wider">
                                        Profissional
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-emerald-900 uppercase tracking-wider">
                                        Tipo de Cuidado
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-emerald-900 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-emerald-900 uppercase tracking-wider">
                                        Descrição
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {agendamentos.map((agendamento) => (
                                    <tr key={agendamento.id} className="hover:bg-emerald-50 transition-colors">
                                        <td className="px-4 py-3 text-sm text-gray-900">
                                            <div className="flex items-center">
                                                <Clock className="w-4 h-4 mr-2 text-emerald-600" />
                                                {formatarPeriodo(agendamento.data_hora_inicio, agendamento.data_hora_fim)}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-700">
                                            <div className="flex items-center">
                                                <User className="w-4 h-4 mr-2 text-emerald-600" />
                                                {agendamento.profissional_nome || 'Não atribuído'}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-700">
                                            {agendamento.tipo_cuidado || agendamento.tipo || 'N/A'}
                                        </td>
                                        <td className="px-4 py-3 text-sm">
                                            <span
                                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                                                    STATUS_COLORS[agendamento.status] || 'bg-gray-100 text-gray-800 border-gray-300'
                                                }`}
                                            >
                                                {agendamento.status || 'N/A'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-700">
                                            {agendamento.descricao || '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile - Cards */}
                    <div className="md:hidden space-y-4">
                        {agendamentos.map((agendamento) => (
                            <div
                                key={agendamento.id}
                                className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-200 p-4"
                            >
                                <div className="flex items-start justify-between mb-3">
                                    <div className="flex-1">
                                        <div className="flex items-center mb-2">
                                            <Clock className="w-4 h-4 mr-2 text-emerald-600 flex-shrink-0" />
                                            <span className="text-sm font-semibold text-gray-900">
                                                {formatarPeriodo(agendamento.data_hora_inicio, agendamento.data_hora_fim)}
                                            </span>
                                        </div>
                                        <div className="flex items-center mb-2">
                                            <User className="w-4 h-4 mr-2 text-emerald-600 flex-shrink-0" />
                                            <span className="text-sm text-gray-700">
                                                {agendamento.profissional_nome || 'Não atribuído'}
                                            </span>
                                        </div>
                                        {agendamento.tipo_cuidado && (
                                            <div className="text-sm text-gray-600 mb-2">
                                                <strong>Tipo:</strong> {agendamento.tipo_cuidado}
                                            </div>
                                        )}
                                    </div>
                                    <span
                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border flex-shrink-0 ${
                                            STATUS_COLORS[agendamento.status] || 'bg-gray-100 text-gray-800 border-gray-300'
                                        }`}
                                    >
                                        {agendamento.status || 'N/A'}
                                    </span>
                                </div>
                                {agendamento.descricao && (
                                    <div className="pt-3 border-t border-gray-200">
                                        <p className="text-sm text-gray-600">{agendamento.descricao}</p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </Card>
            )}
        </div>
    );
};

export default PacienteAgendamentosTab;

